import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, ShoppingBag, Loader2, RefreshCw, Plus, Phone, MapPin,
  Clock, ChefHat, CheckCircle2, Truck, PackageCheck, XCircle, AlertCircle,
  Search, ChevronRight, MessageCircle, CreditCard,
} from "lucide-react";
import { pedidosApi, Pedido, EstadoPedido } from "../../lib/api";
import { useInicio } from "../../lib/permisos";
import { PedidoDetalleModal } from "./PedidoDetalleModal";
import { NuevoPedidoModal } from "./NuevoPedidoModal";

const COLUMNAS: Array<{
  estado: EstadoPedido;
  titulo: string;
  icon: React.ReactNode;
  color: string;
  siguiente?: EstadoPedido;
  ctaSiguiente?: string;
}> = [
  { estado: "nuevo", titulo: "Nuevos", icon: <AlertCircle className="size-4" />, color: "amber", siguiente: "confirmado", ctaSiguiente: "Confirmar" },
  { estado: "confirmado", titulo: "Confirmados", icon: <CheckCircle2 className="size-4" />, color: "blue", siguiente: "en_preparacion", ctaSiguiente: "→ Cocina" },
  { estado: "en_preparacion", titulo: "En cocina", icon: <ChefHat className="size-4" />, color: "purple", siguiente: "listo", ctaSiguiente: "Marcar listo" },
  { estado: "listo", titulo: "Listos", icon: <PackageCheck className="size-4" />, color: "indigo", siguiente: "en_camino", ctaSiguiente: "En camino" },
  { estado: "en_camino", titulo: "En camino", icon: <Truck className="size-4" />, color: "emerald", siguiente: "entregado", ctaSiguiente: "Entregado" },
];

function formatUSD(n: string | number): string {
  const num = typeof n === "string" ? Number(n) : n;
  if (isNaN(num)) return "—";
  return new Intl.NumberFormat("es-VE", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(num);
}

function timeAgo(d: string): string {
  const diff = Date.now() - new Date(d).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ${min % 60}min`;
  return `${Math.floor(h / 24)}d`;
}

export function DeliveryAdminPage() {
  const inicio = useInicio();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [debSearch, setDebSearch] = useState("");
  const [verEntregados, setVerEntregados] = useState(false);
  const [seleccionado, setSeleccionado] = useState<number | null>(null);
  const [crear, setCrear] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const stats = useQuery({
    queryKey: ["pedidos", "stats"],
    queryFn: () => pedidosApi.stats(),
    refetchInterval: 30_000,
  });

  const pedidos = useQuery({
    queryKey: ["pedidos", "list", debSearch, verEntregados],
    queryFn: () => pedidosApi.list({
      estado: verEntregados ? undefined : ["nuevo", "confirmado", "en_preparacion", "listo", "en_camino"],
      busqueda: debSearch || undefined,
      limit: 200,
    }),
    refetchInterval: 20_000,
  });

  const cambiarMut = useMutation({
    mutationFn: ({ id, estado, nota }: { id: number; estado: EstadoPedido; nota?: string }) =>
      pedidosApi.cambiarEstado(id, { estado, nota }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pedidos"] });
    },
  });

  // Agrupar por estado
  const porEstado = new Map<EstadoPedido, Pedido[]>();
  for (const e of COLUMNAS.map((c) => c.estado)) porEstado.set(e, []);
  porEstado.set("entregado", []);
  porEstado.set("cancelado", []);
  for (const p of pedidos.data?.items || []) {
    if (!porEstado.has(p.estado)) porEstado.set(p.estado, []);
    porEstado.get(p.estado)!.push(p);
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <div className="mx-auto max-w-[1600px] px-6 py-8 animate-fade-in">

        <div className="flex items-center justify-between mb-5">
          <Link to={inicio} className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800 transition-colors">
            <ArrowLeft className="size-4" /> Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/articulos-config" className="text-xs text-brand-700 hover:text-brand-900 inline-flex items-center gap-1">
              Artículos <ChevronRight className="size-3" />
            </Link>
            <Link to="/pasarelas-pago" className="text-xs text-brand-700 hover:text-brand-900 inline-flex items-center gap-1">
              <CreditCard className="size-3" /> Pasarelas
            </Link>
          </div>
        </div>

        {/* Header */}
        <header className="flex items-end justify-between mb-5">
          <div className="flex items-center gap-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center shadow-sm">
              <ShoppingBag className="size-6" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold text-ink-800 leading-tight">Pedidos delivery</h1>
              <p className="text-sm text-ink-500 mt-0.5">Tablero de gestión en tiempo real</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { stats.refetch(); pedidos.refetch(); }}
              className="px-3 py-2 rounded-lg bg-white border border-ink-200 hover:bg-ink-50 text-sm font-medium inline-flex items-center gap-2">
              <RefreshCw className={`size-4 ${pedidos.isFetching ? "animate-spin" : ""}`} />
              Refrescar
            </button>
            <button onClick={() => setCrear(true)}
              className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="size-4" /> Nuevo pedido
            </button>
          </div>
        </header>

        {/* KPIs */}
        {stats.data && (
          <div className="grid grid-cols-2 tab:grid-cols-3 md:grid-cols-5 gap-3 mb-5">
            <KPI label="Pedidos hoy" valor={stats.data.pedidosHoy} icon={<ShoppingBag className="size-4 text-brand-600" />} />
            <KPI label="Activos ahora" valor={stats.data.activos} icon={<Clock className="size-4 text-amber-600" />} highlight />
            <KPI label="Entregados hoy" valor={stats.data.entregadosHoy} icon={<PackageCheck className="size-4 text-emerald-600" />} />
            <KPI label="Cancelados hoy" valor={stats.data.canceladosHoy} icon={<XCircle className="size-4 text-red-600" />} />
            <KPI label="Ventas hoy" valor={formatUSD(stats.data.ventasHoy)} icon={<CreditCard className="size-4 text-brand-600" />} small />
          </div>
        )}

        {/* Buscador */}
        <div className="card p-3 mb-4 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-400" />
            <input type="text" placeholder="Buscar por nombre, teléfono o # de pedido…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="input pl-10 w-full" />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
            <input type="checkbox" checked={verEntregados} onChange={(e) => setVerEntregados(e.target.checked)}
              className="size-4" /> Incluir entregados/cancelados
          </label>
        </div>

        {/* Kanban */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {COLUMNAS.map((col) => {
            const items = porEstado.get(col.estado) || [];
            return (
              <Columna key={col.estado} col={col} items={items}
                onClick={(id) => setSeleccionado(id)}
                onCambiar={(id, estado) => cambiarMut.mutate({ id, estado })}
                loading={cambiarMut.isPending} />
            );
          })}
        </div>

        {/* Sección entregados/cancelados (si se activa) */}
        {verEntregados && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <ColumnaSimple titulo="Entregados" icon={<PackageCheck className="size-4 text-emerald-600" />} color="emerald"
              items={porEstado.get("entregado") || []} onClick={(id) => setSeleccionado(id)} />
            <ColumnaSimple titulo="Cancelados" icon={<XCircle className="size-4 text-red-600" />} color="red"
              items={porEstado.get("cancelado") || []} onClick={(id) => setSeleccionado(id)} />
          </div>
        )}

        {pedidos.isLoading && (
          <div className="text-center py-12 text-ink-400">
            <Loader2 className="size-6 animate-spin mx-auto mb-2" />
            Cargando pedidos…
          </div>
        )}

        {/* Modales */}
        {seleccionado && (
          <PedidoDetalleModal pedidoId={seleccionado} onClose={() => setSeleccionado(null)} />
        )}
        {crear && (
          <NuevoPedidoModal onClose={() => { setCrear(false); pedidos.refetch(); stats.refetch(); }} />
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
function KPI({ label, valor, icon, highlight, small }: {
  label: string; valor: string | number; icon: React.ReactNode;
  highlight?: boolean; small?: boolean;
}) {
  return (
    <div className={`card p-3 ${highlight ? "border-amber-300" : ""}`}>
      <div className="flex items-center gap-2 text-xs text-ink-500 mb-1">{icon}{label}</div>
      <div className={`font-display font-bold text-ink-800 ${small ? "text-base" : "text-xl"}`}>
        {valor}
      </div>
    </div>
  );
}

function Columna({ col, items, onClick, onCambiar, loading }: {
  col: typeof COLUMNAS[number];
  items: Pedido[];
  onClick: (id: number) => void;
  onCambiar: (id: number, estado: EstadoPedido) => void;
  loading: boolean;
}) {
  return (
    <div className="bg-white rounded-xl border border-ink-200 overflow-hidden flex flex-col min-h-[200px]">
      <div className={`px-3 py-2 border-b border-ink-200 flex items-center justify-between bg-${col.color}-50 text-${col.color}-700`}>
        <div className="flex items-center gap-2 font-medium text-sm">
          {col.icon}{col.titulo}
        </div>
        <span className="text-xs font-bold bg-white px-2 py-0.5 rounded-full">{items.length}</span>
      </div>
      <div className="p-2 space-y-2 flex-1 max-h-[600px] overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-xs text-ink-400 text-center py-6">Sin pedidos</div>
        ) : items.map((p) => (
          <PedidoCard key={p.id} pedido={p} onClick={() => onClick(Number(p.id))}
            ctaSiguiente={col.ctaSiguiente}
            onSiguiente={col.siguiente ? () => onCambiar(Number(p.id), col.siguiente!) : undefined}
            loading={loading} />
        ))}
      </div>
    </div>
  );
}

function ColumnaSimple({ titulo, icon, color, items, onClick }: {
  titulo: string; icon: React.ReactNode; color: string;
  items: Pedido[]; onClick: (id: number) => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-ink-200 overflow-hidden">
      <div className={`px-3 py-2 border-b border-ink-200 flex items-center gap-2 bg-${color}-50 text-${color}-700 font-medium text-sm`}>
        {icon}{titulo}
        <span className="text-xs font-bold bg-white px-2 py-0.5 rounded-full ml-auto">{items.length}</span>
      </div>
      <div className="p-2 space-y-2 max-h-[400px] overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-xs text-ink-400 text-center py-4">—</div>
        ) : items.slice(0, 20).map((p) => (
          <button key={p.id} onClick={() => onClick(Number(p.id))}
            className="w-full text-left px-3 py-2 rounded-lg border border-ink-200 hover:border-brand-300 hover:bg-brand-50/30 transition-colors text-xs">
            <div className="flex items-center justify-between">
              <span className="font-mono text-ink-500">{p.numeroPedido}</span>
              <span className="font-medium text-ink-800">{formatUSD(p.total)}</span>
            </div>
            <div className="text-ink-700 truncate">{p.nombreCliente}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function PedidoCard({ pedido, onClick, ctaSiguiente, onSiguiente, loading }: {
  pedido: Pedido;
  onClick: () => void;
  ctaSiguiente?: string;
  onSiguiente?: () => void;
  loading: boolean;
}) {
  const pagoColor = pedido.estadoPago === "confirmado" ? "emerald"
    : pedido.estadoPago === "rechazado" ? "red"
    : pedido.estadoPago === "reportado" ? "amber" : "ink";
  return (
    <div className="bg-white rounded-lg border border-ink-200 p-3 hover:shadow-md transition-shadow">
      <button onClick={onClick} className="w-full text-left">
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono text-[10px] text-ink-400">{pedido.numeroPedido}</span>
          <span className="text-[10px] text-ink-500 inline-flex items-center gap-1">
            <Clock className="size-3" />{timeAgo(pedido.horaSolicitud)}
          </span>
        </div>
        <div className="font-medium text-ink-800 text-sm truncate">{pedido.nombreCliente}</div>
        {pedido.telefono && (
          <div className="text-xs text-ink-500 inline-flex items-center gap-1 mt-0.5">
            <Phone className="size-3" />{pedido.telefono}
          </div>
        )}
        {pedido.tipoEntrega === "delivery" && pedido.nombreZona && (
          <div className="text-xs text-brand-600 font-medium mt-1">{pedido.nombreZona}</div>
        )}
        {pedido.tipoEntrega === "delivery" && pedido.direccionEntrega && (
          <div className="text-xs text-ink-500 mt-0.5 inline-flex items-start gap-1">
            <MapPin className="size-3 mt-0.5 shrink-0" />
            <span className="line-clamp-2">{pedido.direccionEntrega}</span>
          </div>
        )}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-ink-100">
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded bg-${pagoColor}-50 text-${pagoColor}-700`}>
            💳 {pedido.estadoPago}
          </span>
          <span className="font-bold text-ink-800 text-sm">{formatUSD(pedido.total)}</span>
        </div>
        <div className="text-[10px] text-ink-500 mt-1">{pedido.lineas?.length || 0} items</div>
      </button>
      {ctaSiguiente && onSiguiente && (
        <button onClick={onSiguiente} disabled={loading}
          className="w-full mt-2 px-2 py-1.5 rounded bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium disabled:opacity-50 inline-flex items-center justify-center gap-1">
          {loading ? <Loader2 className="size-3 animate-spin" /> : <ChevronRight className="size-3" />}
          {ctaSiguiente}
        </button>
      )}
    </div>
  );
}
